import { useMemo, useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQR } from "../../utils/hooks/useQR";
import { useAuth } from "../../utils/Auth";
import { useNavigation } from "../../utils/NavigationContext";
import API from "../../API";
import { filterCompleteConnectButtons, getConnectButtonDefaultIcon, resolveConnectButtonHref } from "../../lib/connectButtons";
import { buildConnectTrackingPayload, sendConnectButtonEvent, shouldIgnoreRapidTap } from "../../lib/connectButtonAnalytics";
import styles from "./QrConnectPage.module.css";

const BUTTON_EVENT_TYPES = {
  phone: "phone_clicked",
  email: "email_clicked",
  sms: "sms_clicked",
};

export default function QrConnectPage() {
  const { setShowNavigation } = useNavigation();
  const { qr_data, currentUser } = useAuth();
  const { id: qrId } = useParams();
  const [searchParams] = useSearchParams();
  const cp = searchParams.get("cp") || "";
  const [continueUrl, setContinueUrl] = useState(null);

  // Load QR data into global state
  useQR("details", qrId);

  // Fetch the continue URL from Supabase using the cp (hash) param
  useEffect(() => {
    setShowNavigation(false);
    const fetchContinueUrl = async () => {
      if (cp) {
        const url = await API.getContinueUrl(cp);
        setContinueUrl(url);
      }
    };
    fetchContinueUrl();
  }, [cp, setShowNavigation]);

  const connect = qr_data?.data?.connect || {};
  const header = connect?.header || {};
  const buttons = useMemo(() => Array.isArray(connect?.buttons) ? connect.buttons : [], [connect?.buttons]);
  const defaultUrl = connect?.defaultUrl || "";

  const sanitizeUrl = (url, fallback) => {
    if (!url) return fallback || "";
    if (url.startsWith("data:text/html")) return fallback || "";
    return url;
  };

  // This is both the Continue Browsing destination and the source attributed to
  // every QR Connect interaction.
  const sourceUrl = useMemo(() => sanitizeUrl(continueUrl, defaultUrl), [continueUrl, defaultUrl]);

  const useHeaderImage = useMemo(() => header.use_image, [header.use_image]);
  const hideHeaderText = useMemo(() => header.hide_text, [header.hide_text]);
  const getAnalyticsAuthToken = useCallback(async () => {
    if (!currentUser) {
      return "";
    }

    try {
      return await currentUser.getIdToken();
    } catch (err) {
      console.error("Failed to get auth token for connect analytics:", err);
      return "";
    }
  }, [currentUser]);

  const computedButtons = useMemo(() => {
    const originUrl = typeof window !== "undefined" ? window.location.href : "";

    return filterCompleteConnectButtons(buttons)
      .map((button) => {
        const color = button.color || "#000000";
        const background = button.background || "#ffffff";
        const border = `${button.border?.width || 1}px ${button.border?.style || 'none'} ${button.border?.color || '#dddddd'}`;
        const borderRadius = `${button.border?.radius || 8}px`;
        const boxShadow = `${button.box_shadow?.x || 0}px ${button.box_shadow?.y || 1}px ${button.box_shadow?.blur || 3}px ${button.box_shadow?.color || '#dddddd'}`;
        const text = button.text || "";
        const type = button.type || "";
        const value = button.value || "";
        const { href, target, rel } = resolveConnectButtonHref(button, {
          qrId,
          safeContinueUrl: sourceUrl,
          defaultUrl,
          originUrl,
          utmSource: sourceUrl,
          trackingParams: {
            utm_source: sourceUrl,
            utm_medium: "qr",
          },
          // Continue must return directly to the URL retrieved with cp. Other
          // HTTP destinations stay on the server-side click tracking route.
          connectClickOrigin: button.type === "continue" ? "" : undefined,
        });

        const icon = button.icon || getConnectButtonDefaultIcon(button);
        return { 
          id: button.id, 
          text, 
          color, 
          background, 
          href, 
          target, 
          rel, 
          type, 
          platform: button.platform || "",
          value, 
          utmSource: sourceUrl,
          icon,
          border,
          borderRadius,
          boxShadow
        };
      });
  }, [buttons, sourceUrl, defaultUrl, qrId]);

  const handleButtonClick = async (e, button) => {
    const originUrl = typeof window !== "undefined" ? window.location.href : "";
    const trackingPayload = buildConnectTrackingPayload({
      originUrl,
      utm_source: button.utmSource || "",
      utm_medium: "qr",
    });

    if (button.type === 'share') {
      e.preventDefault();
      if (shouldIgnoreRapidTap(`${qrId}:${button.id}:share_opened`)) {
        return;
      }

      if (!navigator.share) {
        console.warn('Web Share API not supported');
        alert('Share is not supported on this device');
        return;
      }
      const shareUrl = button.href !== '#' ? button.href : '';
      const authToken = await getAnalyticsAuthToken();

      sendConnectButtonEvent(undefined, {
        qrId,
        buttonId: button.id,
        event: 'share_opened',
        authToken,
        ...trackingPayload,
      });

      try {
        const shareData = {
          title: button.text || 'Share',
        };

        if (shareUrl) {
          shareData.url = shareUrl;
        }

        await navigator.share(shareData);

        sendConnectButtonEvent(undefined, {
          qrId,
          buttonId: button.id,
          event: 'share_completed',
          authToken,
          ...trackingPayload,
        });
      } catch (err) {
        console.error('Share cancelled or failed:', err);

        sendConnectButtonEvent(undefined, {
          qrId,
          buttonId: button.id,
          event: 'share_cancelled',
          authToken,
          ...trackingPayload,
        });
      }

      return;
    }

    if (button.href === '#') {
      e.preventDefault();
      return;
    }

    if (button.type === 'redirect' || button.type === 'continue') {
      if (shouldIgnoreRapidTap(`${qrId}:${button.id}:click`)) {
        e.preventDefault();
        return;
      }

      if (button.type === 'continue') {
        // Keep navigation independent of analytics: the browser follows the
        // direct source URL while the click event is sent best-effort.
        void getAnalyticsAuthToken().then((authToken) => {
          sendConnectButtonEvent(undefined, {
            qrId,
            buttonId: button.id,
            event: 'continue_clicked',
            authToken,
            ...trackingPayload,
          });
        });
      }

      // Let the browser navigate to the tracked link and follow its redirect.
      // Unlike fetch(), this navigation is not blocked by the destination's CORS policy.
      return;
    }

    const buttonEvent = BUTTON_EVENT_TYPES[button.type];
    if (buttonEvent) {
      if (shouldIgnoreRapidTap(`${qrId}:${button.id}:${buttonEvent}`)) {
        e.preventDefault();
        return;
      }

      e.preventDefault();
      const authToken = await getAnalyticsAuthToken();
      sendConnectButtonEvent(undefined, {
        qrId,
        buttonId: button.id,
        event: buttonEvent,
        authToken,
        ...trackingPayload,
      });

      window.location.href = button.href;
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.connect}>
          <h1
            className={styles.headerCard}
            style={{ 
              color: header.color || undefined, 
              backgroundColor: header.background || "transparent",
              backgroundImage: useHeaderImage ? `url(${header.image})` : null
            }}
          >
            {!hideHeaderText && header.text}
          </h1>
          <div className={styles.buttonsGrid}>
            {computedButtons.map((b) => (
              <a
                key={b.id}
                href={b.href}
                target={b.target}
                rel={b.rel}
                className={styles.buttonElement}
                style={{ 
                  color: b.color, 
                  backgroundColor: b.background,
                  border: b.border,
                  borderRadius: b.borderRadius,
                  boxShadow: b.boxShadow
                }}
                onClick={(e) => handleButtonClick(e, b)}
              >
                <span className={styles.buttonContent}>
                  <span className={styles.buttonIconLeft} aria-hidden>
                    {b.type !== 'continue' && b.icon ? <i className={b.icon} /> : null}
                  </span>
                  <span className={styles.buttonText}>
                    {b.text || ""}
                    {b.type === 'continue' && b.icon ? (
                      <i className={`${b.icon} ${styles.inlineIconRight}`} aria-hidden />
                    ) : null}
                  </span>
                  <span className={styles.buttonIconRight} aria-hidden />
                </span>
              </a>
            ))}
          </div>
          <div className={styles.poweredBy}>
            Powered by{" "}
            <a href="https://lqr.ai/qm2Yeb" target="_blank" rel="noopener noreferrer">
              LiquidQR
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
